import React, { useEffect, useMemo, useRef, useState } from 'react';
import { mergeRequiredWithUploads, displaySubtypeForFile, categoryKeyForDocument } from '../requiredDocuments';
import DocumentsTabSource from './DocumentsTabSource';
import { mvpApi } from '../../../services/api';
import { formatDocumentFilenameForCaseView } from '../../../utils/uploadFileDisplayName';

function mapCategoryId(categoryId) {
  if (categoryId === 'passport') return 'passport';
  if (categoryId === 'proof_of_residency') return 'residence';
  if (categoryId === 'criminal_record') return 'criminal';
  if (categoryId === 'asylum_receipt') return 'asylum';
  return 'other';
}

function toReviewStatus(status) {
  const s = String(status || 'pending').toLowerCase();
  if (s === 'accepted' || s === 'rejected' || s === 'pending') return s;
  return 'pending';
}

function getUploadDocumentType(category, docType) {
  const dt = String(docType || '').trim().toLowerCase();
  if (category === 'passport') {
    if (dt === 'nationalid' || dt === 'national_id_card_id') return 'national_id_card_id';
    if (dt === 'traveldoc' || dt === 'travel_document_id') return 'travel_document_id';
    if (dt === 'cedulainscripcion' || dt === 'cedula_de_inscripcion_id') return 'cedula_de_inscripcion_id';
    if (dt === 'other' || dt === 'other_id') return 'other_id';
    return 'passport_id';
  }
  if (category === 'criminal') return 'criminal_record_certificate';
  if (category === 'asylum') {
    if (dt === 'card') return 'red_card_id';
    if (dt === 'resolution') return 'denial_resolution_id';
    return 'application_receipt_id';
  }
  if (category === 'residence') {
    const map = {
      padron: 'padron_municipal_registration_id',
      rental: 'rental_contract_id',
      medical: 'medical_record_id',
      school: 'childrens_school_enrollment_id',
      bank: 'bank_statement_id',
      utility: 'utility_bill_id',
      payslip: 'payslip_work_contract_id',
      transfer: 'money_transfer_receipt_id',
      course: 'course_training_certificate_id',
      transport: 'transport_ticket_id',
      police: 'police_report_id',
      official: 'official_letter_notification_id',
      other: 'other',
    };
    return map[dt] || 'other';
  }
  if (category === 'employment') {
    const map = {
      contract: 'work_contract_id',
      payslip: 'nomina_id',
      vidalaboral: 'vida_laboral_id',
      joboffer: 'job_offer_letter_id',
      other: 'other_documents_id',
      autonomo: 'self_employment_id',
    };
    return map[dt] || 'other_documents_id';
  }
  if (category === 'family') {
    const map = {
      marriage: 'marriage_certificate_id',
      libro: 'libro_de_familia_id',
      birth: 'childrens_birth_certificates_id',
      cohabitation: 'proof_of_cohabitation_id',
      school: 'school_enrollment_id',
      other: 'other_documents_id',
    };
    return map[dt] || 'other_documents_id';
  }
  return 'other_supporting';
}

function isBackendDocId(id) {
  const s = String(id || '');
  if (!s) return false;
  // Local temp ids are prefixed like pas-/res-/fam-/emp-/asy-/oth-/vul-
  return !/^(pas|res|fam|emp|asy|oth|vul)-/i.test(s);
}

function sameLogicalDoc(a, b) {
  return (
    String(a?.category || '') === String(b?.category || '') &&
    String(a?.docType || '') === String(b?.docType || '')
  );
}

function toDocState(mergedCategories, rawDocuments = [], publicCaseId) {
  const fmt = (fn) => formatDocumentFilenameForCaseView(fn, publicCaseId) || fn;
  const fromMerged = (mergedCategories || []).flatMap((cat) =>
    (cat.files || []).map((f, idx) => ({
      // Source component expects asylum subtypes as: receipt | card | resolution
      // while backend stores: asylum_receipt | asylum_card | asylum_resolution.
      // Normalize here so uploaded asylum files render in the correct blocks.
      normalizedDocType:
        String(cat.id) === 'asylum_receipt'
          ? (() => {
            const raw = String(f.document_type || '').trim().toLowerCase();
            if (raw === 'application_receipt_id') return 'receipt';
            if (raw === 'red_card_id') return 'card';
            if (raw === 'denial_resolution_id') return 'resolution';
            return raw;
          })()
          : null,
      identityNormalizedDocType:
        String(cat.id) === 'passport'
          ? (() => {
            const raw = String(f.document_type || '').trim().toLowerCase();
            if (raw === 'passport_id') return 'passport';
            if (raw === 'national_id_card_id' || raw === 'nationalid') return 'nationalId';
            if (raw === 'travel_document_id' || raw === 'traveldoc') return 'travelDoc';
            if (
              raw === 'cedula_de_inscripcion_id' ||
              raw === 'cédula_de_inscripción_id' ||
              raw === 'cedulainscripcion'
            ) {
              return 'cedulaInscripcion';
            }
            if (raw === 'other_id' || raw === 'other_identity') return 'other';
            return '';
          })()
          : null,
      employmentNormalizedDocType:
        String(cat.id) === 'employment'
          ? (() => {
            const raw = String(f.document_type || '').trim().toLowerCase();
            if (raw === 'work_contract_id') return 'contract';
            if (raw === 'nomina_id') return 'payslip';
            if (raw === 'vida_laboral_id') return 'vidalaboral';
            if (raw === 'job_offer_letter_id') return 'joboffer';
            if (raw === 'self_employment_id') return 'autonomo';
            return 'other';
          })()
          : null,
      id: String(f.id || `${cat.id}-${idx}`),
      name: fmt(f.filename) || cat.name || 'Document',
      description: cat.description || '',
      category:
        String(cat.id) === 'other' &&
          String(f.supporting_description || '').trim().toLowerCase() === 'vulnerability_report'
          ? 'vulnerability'
          : mapCategoryId(cat.id),
      docType:
        (String(cat.id) === 'passport'
          ? (() => {
            const normalized = (() => {
              const raw = String(f.document_type || '').trim().toLowerCase();
              if (raw === 'passport_id') return 'passport';
              if (raw === 'national_id_card_id' || raw === 'nationalid') return 'nationalId';
              if (raw === 'travel_document_id' || raw === 'traveldoc') return 'travelDoc';
              if (
                raw === 'cedula_de_inscripcion_id' ||
                raw === 'cédula_de_inscripción_id' ||
                raw === 'cedulainscripcion'
              ) {
                return 'cedulaInscripcion';
              }
              if (raw === 'other_id' || raw === 'other_identity') return 'other';
              return '';
            })();
            return normalized;
          })()
          : '') ||
        (String(cat.id) === 'asylum_receipt'
          ? (() => {
            const raw = String(f.document_type || '').trim().toLowerCase();
            if (raw === 'application_receipt_id') return 'receipt';
            if (raw === 'red_card_id') return 'card';
            if (raw === 'denial_resolution_id') return 'resolution';
            return '';
          })()
          : '') ||
        (String(cat.id) === 'employment'
          ? (() => {
            const raw = String(f.document_type || '').trim().toLowerCase();
            if (raw === 'work_contract_id') return 'contract';
            if (raw === 'nomina_id') return 'payslip';
            if (raw === 'vida_laboral_id') return 'vidalaboral';
            if (raw === 'job_offer_letter_id') return 'joboffer';
            if (raw === 'self_employment_id') return 'autonomo';
            return 'other';
          })()
          : '') ||
        f.residency_doc_label ||
        f.document_type ||
        displaySubtypeForFile(f) ||
        undefined,
      uploaded: true,
      uploadedAt: f.created_at || undefined,
      uploadedFile: fmt(f.filename) || undefined,
      reviewStatus: toReviewStatus(f.review_status),
      rejectionReason: f.rejection_reason || undefined,
      supportingDescription: f.supporting_description || undefined,
      viewed: false,
    })),
  );

  const existingIds = new Set(fromMerged.map((doc) => String(doc.id)));
  const directCategories = new Set(['employment', 'family', 'vulnerability']);
  const fromRaw = (rawDocuments || [])
    .filter((d) => directCategories.has(categoryKeyForDocument(d)))
    .map((d, idx) => {
      const category = categoryKeyForDocument(d);
      const rawType = String(d?.document_type || '').trim().toLowerCase();
      const docType =
        category === 'employment'
          ? rawType === 'work_contract_id'
            ? 'contract'
            : rawType === 'nomina_id'
              ? 'payslip'
              : rawType === 'vida_laboral_id'
                ? 'vidalaboral'
                : rawType === 'job_offer_letter_id'
                  ? 'joboffer'
                  : rawType === 'self_employment_id'
                    ? 'autonomo'
                    : 'other'
          : category === 'family'
            ? rawType === 'marriage_certificate_id'
              ? 'marriage'
              : rawType === 'libro_de_familia_id'
                ? 'libro'
                : rawType === 'childrens_birth_certificates_id'
                  ? 'birth'
                  : rawType === 'proof_of_cohabitation_id'
                    ? 'cohabitation'
                    : rawType === 'school_enrollment_id'
                      ? 'school'
                      : 'other'
            : 'other';
      return {
        id: String(d?.id || `${category}-${idx}`),
        name: fmt(d?.filename) || 'Document',
        description: '',
        category,
        docType,
        uploaded: true,
        uploadedAt: d?.created_at || undefined,
        uploadedFile: fmt(d?.filename) || undefined,
        reviewStatus: toReviewStatus(d?.review_status),
        rejectionReason: d?.rejection_reason || undefined,
        supportingDescription: d?.supporting_description || undefined,
        viewed: false,
      };
    })
    .filter((doc) => !existingIds.has(String(doc.id)));

  return [...fromMerged, ...fromRaw];
}

function buildCategoryReview(docs) {
  const base = {
    passport: 'pending',
    residence: 'pending',
    criminal: 'pending',
    asylum: 'pending',
    employment: 'pending',
    family: 'pending',
    vulnerability: 'pending',
    other: 'pending',
  };
  (docs || []).forEach((d) => {
    if (!d?.category) return;
    base[d.category] = toReviewStatus(d.reviewStatus);
  });
  return base;
}

function mergeDocsWithLocalState(serverDocs, localDocs) {
  const localById = new Map((localDocs || []).map((doc) => [String(doc.id), doc]));
  const merged = (serverDocs || []).map((doc) => {
    const local = localById.get(String(doc.id));
    if (!local) return doc;
    return {
      ...doc,
      reviewStatus: local.reviewStatus ?? doc.reviewStatus,
      rejectionReason: local.rejectionReason ?? doc.rejectionReason,
      viewed: local.viewed ?? doc.viewed,
    };
  });

  const localOnlyPending = (localDocs || []).filter((doc) => {
    const id = String(doc?.id || '');
    return id && !isBackendDocId(id);
  }).filter((doc) => !(serverDocs || []).some((serverDoc) => sameLogicalDoc(serverDoc, doc)));

  return [...merged, ...localOnlyPending];
}

function parseDateStringToIso(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s]|$)/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const dmyMatch = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmyMatch) {
    const day = dmyMatch[1].padStart(2, '0');
    const month = dmyMatch[2].padStart(2, '0');
    return `${dmyMatch[3]}-${month}-${day}`;
  }
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
}

function toIsoDateOrEmpty(raw) {
  return parseDateStringToIso(raw);
}

function normalizeSex(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return '';
  if (s === 'male' || s === 'm' || s === 'hombre') return 'male';
  if (s === 'female' || s === 'f' || s === 'mujer') return 'female';
  if (s === 'undetermined' || s === 'x' || s === 'indeterminado') return 'undetermined';
  return '';
}

function mapPassportFieldsToAutosave(passportIdFields) {
  const f = passportIdFields || {};
  const hasPassport = String(f.hasPassport || '').trim().toLowerCase() !== 'no';
  const docTypeRaw = String(f.docType || '').trim();
  const tipoDocumento =
    hasPassport
      ? 'passport'
      : docTypeRaw === 'nationalId'
        ? 'national_id'
        : docTypeRaw === 'travelDoc'
          ? 'travel_doc'
          : docTypeRaw === 'cedulaInscripcion'
            ? 'cedula_inscripcion'
            : docTypeRaw || 'other';
  return {
    tiene_pasaporte: hasPassport,
    tipo_documento_identidad: tipoDocumento,
    numero_pasaporte: String(f.passportNumber || '').trim(),
    passport_number: String(f.passportNumber || '').trim(),
    numero_documento_identidad: String(f.nationalIdNumber || '').trim(),
    national_id_document_number: String(f.nationalIdNumber || '').trim(),
    travel_document_number: String(f.travelDocumentNumber || '').trim(),
    registration_document_number: String(f.registrationDocumentNumber || '').trim(),
    other_document_number: String(f.otherDocumentNumber || '').trim(),
    caducidad_documento: toIsoDateOrEmpty(f.docExpiry),
    pais_emisor_documento_identidad: String(f.docIssuingCountry || '').trim(),
    nombre_documento_identidad: String(f.docFullName || '').trim(),
    fecha_nacimiento: toIsoDateOrEmpty(f.dob),
    sexo: String(f.sex || '').trim(),
    pais_nacimiento: String(f.countryOfBirth || '').trim(),
    lugar_nacimiento: String(f.placeOfBirth || '').trim(),
  };
}

function mapEmploymentFieldsToAutosave(employmentFields) {
  const f = employmentFields || {};
  return {
    work_type: String(f.workType || '').trim(),
    self_employment_activity: String(f.selfEmploymentActivity || '').trim(),
  };
}

export default function DocumentsTab({
  userId,
  sessionId,
  publicCaseId,
  documents = [],
  archivedDocuments = [],
  collectedFields = {},
  asylumApplicant = '',
  priorAsylumApplication,
  qualifyingGround = '',
  subRoute = '',
  highlightDocId = null,
  onClearHighlight,
  onRefresh,
}) {
  const hasAsylumUploads = useMemo(
    () =>
      (documents || []).some((d) => {
        const section = String(d?.section_id || '').trim().toLowerCase();
        const docType = String(d?.document_type || '').trim().toLowerCase();
        return (
          section === 'asylum_section' ||
          docType === 'application_receipt_id' ||
          docType === 'red_card_id' ||
          docType === 'denial_resolution_id'
        );
      }),
    [documents],
  );

  const asylumApplicantRaw = String(asylumApplicant || '').trim().toLowerCase();
  const isAsylumApplicant =
    asylumApplicantRaw === 'yes' ||
    asylumApplicantRaw === 'true' ||
    asylumApplicantRaw === '1' ||
    priorAsylumApplication === 'after' ||
    hasAsylumUploads;
  const effectivePriorAsylumApplication = isAsylumApplicant ? 'after' : 'never';

  const identityFromCollected = useMemo(() => {
    const cf = collectedFields || {};
    const pick = (...keys) => {
      for (const key of keys) {
        const value = cf?.[key];
        if (value == null) continue;
        const s = String(value).trim();
        if (s) return s;
      }
      return '';
    };
    const toIsoDate = (raw) => {
      return parseDateStringToIso(raw);
    };
    const hasPassportRaw = String(cf?.tiene_pasaporte ?? cf?.has_passport ?? '').trim().toLowerCase();
    const hasPassport =
      hasPassportRaw === 'false' || hasPassportRaw === 'no' || hasPassportRaw === '0'
        ? 'no'
        : 'yes';
    const rawDocType = pick('tipo_documento_identidad', 'identity_doc_type', 'doc_type').toLowerCase();
    let docType = 'passport';
    if (rawDocType && rawDocType !== 'passport') {
      if (rawDocType === 'nationalid' || rawDocType === 'national_id') docType = 'nationalId';
      else if (rawDocType === 'traveldoc' || rawDocType === 'travel_doc') docType = 'travelDoc';
      else if (rawDocType === 'cedulainscripcion' || rawDocType === 'cedula_inscripcion') docType = 'cedulaInscripcion';
      else docType = 'other';
    }
    return {
      hasPassport,
      passportNumber: pick('passport_number', 'numero_pasaporte', 'identity_doc_number'),
      nationalIdNumber: pick('national_id_document_number', 'numero_documento_identidad', 'identity_document_number'),
      travelDocumentNumber: pick('travel_document_number'),
      registrationDocumentNumber: pick('registration_document_number'),
      otherDocumentNumber: pick('other_document_number'),
      docType,
      docExpiry: toIsoDate(pick('caducidad_documento', 'identity_doc_expiry_at')),
      docIssuingCountry: pick(
        'pais_emisor_documento_identidad',
        'pais_emisor_documento',
        'identity_doc_issuing_country',
      ),
      docFullName: pick('nombre_documento_identidad', 'identity_doc_full_name'),
      dob: toIsoDate(pick('fecha_nacimiento', 'date_of_birth')),
      sex: normalizeSex(pick('sexo', 'sex', 'gender')),
      countryOfBirth: pick('pais_nacimiento', 'country_of_birth'),
      placeOfBirth: pick('lugar_nacimiento', 'place_of_birth'),
    };
  }, [collectedFields]);

  const employmentFromCollected = useMemo(() => {
    const cf = collectedFields || {};
    const pick = (...keys) => {
      for (const key of keys) {
        const value = cf?.[key];
        if (value == null) continue;
        const s = String(value).trim();
        if (s) return s;
      }
      return '';
    };
    const rawWorkType = pick('work_type', 'workType').toLowerCase();
    const workType =
      rawWorkType === 'selfemployed' || rawWorkType === 'self_employed'
        ? 'selfemployed'
        : rawWorkType === 'employed'
          ? 'employed'
          : '';
    const employmentDocs = (documents || []).filter((doc) => {
      const section = String(doc?.section_id || '').trim().toLowerCase();
      const dtype = String(doc?.document_type || '').trim().toLowerCase();
      return (
        section === 'employment_section' ||
        dtype === 'work_contract_id' ||
        dtype === 'nomina_id' ||
        dtype === 'vida_laboral_id' ||
        dtype === 'job_offer_letter_id' ||
        dtype === 'other_documents_id' ||
        dtype === 'self_employment_id'
      );
    });
    const activityFromDocs =
      employmentDocs
        .map((doc) => String(doc?.self_employment_activity || '').trim())
        .find(Boolean) || '';
    return {
      workType,
      selfEmploymentActivity:
        pick('self_employment_activity', 'selfEmploymentActivity') || activityFromDocs,
    };
  }, [collectedFields, documents]);

  const identityFromUploadedDoc = useMemo(() => {
    const identityDoc = (documents || []).find((d) => {
      const section = String(d?.section_id || '').trim().toLowerCase();
      const dt = String(d?.document_type || '').trim().toLowerCase();
      return (
        section === 'identity_section' ||
        dt === 'passport_id' ||
        dt === 'national_id_card_id' ||
        dt === 'travel_document_id' ||
        dt === 'cedula_de_inscripcion_id' ||
        dt === 'other_id'
      );
    });
    const extracted = identityDoc?.extracted_fields && typeof identityDoc.extracted_fields === 'object'
      ? identityDoc.extracted_fields
      : {};
    const asIsoDate = (raw) => {
      return parseDateStringToIso(raw);
    };
    return {
      docFullName: String(
        extracted.full_name || extracted.doc_full_name || extracted.name_on_document || '',
      ).trim(),
      dob: asIsoDate(
        extracted.date_of_birth || extracted.birth_date || extracted.dob || '',
      ),
      docExpiry: asIsoDate(
        extracted.expiry_date || extracted.expiration_date || extracted.document_expiry || '',
      ),
    };
  }, [documents]);

  const merged = useMemo(
    () => mergeRequiredWithUploads(documents, effectivePriorAsylumApplication),
    [documents, effectivePriorAsylumApplication],
  );

  const mappedDocs = useMemo(
    () => toDocState(merged, documents, publicCaseId),
    [merged, documents, publicCaseId],
  );

  const [docs, setDocs] = useState(mappedDocs);
  const [categoryReview, setCategoryReview] = useState(buildCategoryReview(mappedDocs));
  const [countriesOfResidence, setCountriesOfResidence] = useState([]);
  const [criminalCertificates, setCriminalCertificates] = useState([]);
  const autosaveTimerRef = useRef(null);
  const lastAutosavedPayloadRef = useRef('');
  const pendingUploadProcessingRef = useRef(new Set());
  const isRefreshingRef = useRef(false);
  const isInitialMappedSync = useRef(true);
  const [passportIdFields, setPassportIdFields] = useState({
    hasPassport: 'yes',
    passportNumber: '',
    nationalIdNumber: '',
    travelDocumentNumber: '',
    registrationDocumentNumber: '',
    otherDocumentNumber: '',
    docType: 'passport',
    docExpiry: '',
    docIssuingCountry: '',
    docFullName: '',
    dob: '',
    sex: '',
    countryOfBirth: '',
    placeOfBirth: '',
  });
  const [employmentFields, setEmploymentFields] = useState({
    workType: '',
    selfEmploymentActivity: '',
  });

  useEffect(() => {
    setPassportIdFields((prev) => {
      const next = { ...prev, ...identityFromCollected };
      if (!String(next.docFullName || '').trim() && String(identityFromUploadedDoc.docFullName || '').trim()) {
        next.docFullName = identityFromUploadedDoc.docFullName;
      }
      if (!String(next.dob || '').trim() && String(identityFromUploadedDoc.dob || '').trim()) {
        next.dob = identityFromUploadedDoc.dob;
      }
      if (!String(next.docExpiry || '').trim() && String(identityFromUploadedDoc.docExpiry || '').trim()) {
        next.docExpiry = identityFromUploadedDoc.docExpiry;
      }
      return next;
    });
  }, [identityFromCollected, identityFromUploadedDoc]);

  useEffect(() => {
    setEmploymentFields((prev) => ({
      ...prev,
      ...employmentFromCollected,
    }));
  }, [employmentFromCollected]);

  useEffect(() => {
    // Only sync prop to state if we aren't currently in the middle of a refresh we triggered
    if (isRefreshingRef.current) return;

    setDocs((prev) => mergeDocsWithLocalState(mappedDocs, prev));
    setCategoryReview((prev) => ({
      ...prev,
      ...buildCategoryReview(mappedDocs),
    }));
  }, [mappedDocs]);

  const criminalFromDocuments = useMemo(() => {
    const fmtCrim = (fn) => formatDocumentFilenameForCaseView(fn, publicCaseId) || fn;
    const rows = (documents || []).filter((d) => {
      const section = String(d?.section_id || '').trim().toLowerCase();
      const dt = String(d?.document_type || '').trim().toLowerCase();
      return section === 'criminal_record_certificate' || section === 'nie_cert' || dt.includes('criminal');
    });
    const countries = [];
    const certs = [];
    rows.forEach((d, idx) => {
      const country = String(d?.criminal_record_country || d?.supporting_description || '').trim();
      if (!country) return;
      if (!countries.includes(country)) countries.push(country);
      const id = String(d?.id || `crim-${country}-${idx}`);
      const existingIdx = certs.findIndex((c) => c.country === country);
      const nextCert = {
        id,
        country,
        uploaded: true,
        uploadedAt: d?.created_at || undefined,
        uploadedFile: fmtCrim(d?.filename) || undefined,
        reviewStatus: toReviewStatus(d?.review_status),
        rejectionReason: d?.rejection_reason || undefined,
        certRequestedNotReceived: false,
      };
      if (existingIdx >= 0) certs[existingIdx] = nextCert;
      else certs.push(nextCert);
    });
    return { countries, certs };
  }, [documents, publicCaseId]);

  useEffect(() => {
    if (!criminalFromDocuments.countries.length) return;
    setCountriesOfResidence((prev) => {
      const mergedCountries = [...prev];
      criminalFromDocuments.countries.forEach((c) => {
        if (!mergedCountries.includes(c)) mergedCountries.push(c);
      });
      return mergedCountries;
    });
    setCriminalCertificates((prev) => {
      const byCountry = new Map(prev.map((c) => [c.country, c]));
      criminalFromDocuments.certs.forEach((c) => byCountry.set(c.country, c));
      return Array.from(byCountry.values());
    });
  }, [criminalFromDocuments]);

  useEffect(() => {
    if (!userId || !sessionId) return undefined;
    const payload = {
      ...mapPassportFieldsToAutosave(passportIdFields),
      ...mapEmploymentFieldsToAutosave(employmentFields),
    };
    const payloadHash = JSON.stringify(payload);
    if (payloadHash === lastAutosavedPayloadRef.current) return undefined;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      try {
        await mvpApi.autosaveAdminCaseDocumentDetails(userId, sessionId, payload);
        lastAutosavedPayloadRef.current = payloadHash;
        // Don't refresh on every keystroke, only when explicitly needed or after meaningful delay
        // await onRefresh?.(); 
      } catch {
        // Keep UI responsive; details remain in local state and can be re-saved on next edit.
      }
    }, 1000); // Increase debounce to 1s
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [passportIdFields, employmentFields, userId, sessionId]); // Removed onRefresh to prevent unnecessary triggers

  useEffect(() => {
    if (!userId || !sessionId) return;
    let cancelled = false;

    async function syncPendingUploads() {
      const pendingUploads = (docs || []).filter((d) => d?.rawFile instanceof File);
      if (!pendingUploads.length) return;

      let changed = false;
      for (const doc of pendingUploads) {
        if (cancelled) return;
        const pendingId = String(doc.id);
        if (pendingUploadProcessingRef.current.has(pendingId)) continue;
        pendingUploadProcessingRef.current.add(pendingId);

        const file = doc.rawFile;
        const documentType = getUploadDocumentType(doc.category, doc.docType);
        const uploadOpts = {};
        if (String(doc.supportingDescription || '').trim()) {
          uploadOpts.supportingDescription = String(doc.supportingDescription).trim();
        }
        try {
          await mvpApi.uploadAdminCaseDocument(userId, sessionId, file, documentType, uploadOpts);
          if (doc.category === 'passport') {
            try {
              const aiDocumentType = documentType === 'passport_id' ? 'passport' : 'identity';
              const aiRes = await mvpApi.analyzePassportDocument(file, aiDocumentType);
              const extracted = aiRes?.data?.extracted || {};
              setPassportIdFields((prev) => {
                const next = { ...prev };
                if (aiDocumentType === 'passport') {
                  if (String(extracted.full_name || '').trim()) next.docFullName = String(extracted.full_name).trim();
                  if (String(extracted.passport_number || '').trim()) next.passportNumber = String(extracted.passport_number).trim();
                } else if (String(extracted.document_number || '').trim()) {
                  const num = String(extracted.document_number).trim();
                  if (doc.docType === 'nationalId') next.nationalIdNumber = num;
                  else if (doc.docType === 'travelDoc') next.travelDocumentNumber = num;
                  else if (doc.docType === 'cedulaInscripcion') next.registrationDocumentNumber = num;
                  else if (doc.docType === 'other') next.otherDocumentNumber = num;
                }
                if (String(extracted.date_of_birth || '').trim()) next.dob = String(extracted.date_of_birth).trim();
                if (String(extracted.sex || '').trim()) next.sex = String(extracted.sex).trim();
                next.countryOfBirth = String(extracted.country_of_birth ?? '').trim();
                next.placeOfBirth = String(extracted.place_of_birth ?? '').trim();
                return next;
              });
            } catch {
              // ignore
            }
          }
          changed = true;
        } catch {
          // ignore
        } finally {
          pendingUploadProcessingRef.current.delete(pendingId);
        }
      }
      if (!cancelled && changed) {
        isRefreshingRef.current = true;
        try {
          await onRefresh?.();
        } finally {
          isRefreshingRef.current = false;
        }
      }
    }

    syncPendingUploads();
    return () => {
      cancelled = true;
    };
  }, [docs, userId, sessionId]); // Removed onRefresh

  useEffect(() => {
    if (!userId || !sessionId) return;
    if (!mappedDocs.length || !docs.length) return;
    let cancelled = false;

    async function syncStatusAndDeletes() {
      const previousById = new Map(mappedDocs.map((d) => [String(d.id), d]));
      const currentById = new Map(docs.map((d) => [String(d.id), d]));
      let changed = false;

      // Status changes
      for (const [id, prevDoc] of previousById.entries()) {
        if (!isBackendDocId(id)) continue;
        const currentDoc = currentById.get(id);
        if (!currentDoc) continue;
        if (String(prevDoc.reviewStatus) !== String(currentDoc.reviewStatus)) {
          try {
            await mvpApi.updateAdminDocumentStatus(
              userId,
              sessionId,
              id,
              currentDoc.reviewStatus,
              currentDoc.rejectionReason || null,
            );
            changed = true;
          } catch {
            // Reconciliation occurs on refresh below.
          }
        }
      }

      // Deletions
      for (const [id] of previousById.entries()) {
        if (!isBackendDocId(id)) continue;
        if (!currentById.has(id)) {
          try {
            await mvpApi.deleteAdminDocument(userId, sessionId, id);
            changed = true;
          } catch {
            // Reconciliation occurs on refresh below.
          }
        }
      }

      if (!cancelled && changed) {
        isRefreshingRef.current = true;
        try {
          await onRefresh?.();
        } finally {
          isRefreshingRef.current = false;
        }
      }
    }

    syncStatusAndDeletes();
    return () => {
      cancelled = true;
    };
  }, [docs, mappedDocs, userId, sessionId]); // Removed onRefresh

  const showAsylumCategory = isAsylumApplicant;
  const pickCollected = (...keys) => {
    const cf = collectedFields || {};
    for (const key of keys) {
      const value = cf?.[key];
      if (value == null) continue;
      const s = String(value).trim();
      if (s) return s;
    }
    return '';
  };
  const normalizePathway = (value) => {
    const v = String(value || '').trim().toLowerCase();
    if (!v) return '';
    if (v === '7a' || v === 'work') return 'work';
    if (v === '7b' || v === 'family') return 'family';
    if (v === '7c' || v === 'vulnerability') return 'vulnerability';
    return '';
  };
  const uploadedPathway = (() => {
    const hasEmployment = (documents || []).some((d) => categoryKeyForDocument(d) === 'employment');
    if (hasEmployment) return 'work';
    const hasFamily = (documents || []).some((d) => categoryKeyForDocument(d) === 'family');
    if (hasFamily) return 'family';
    const hasVulnerability = (documents || []).some((d) => categoryKeyForDocument(d) === 'vulnerability');
    if (hasVulnerability) return 'vulnerability';
    return '';
  })();
  const normalizedPathway =
    normalizePathway(subRoute) ||
    normalizePathway(qualifyingGround) ||
    normalizePathway(pickCollected('sub_route', 'qualifying_ground', 'ruta_irregular', 'irregular_sub_route')) ||
    uploadedPathway;
  const showEmploymentCategory = !isAsylumApplicant && normalizedPathway === 'work';
  const showFamilyCategory = !isAsylumApplicant && normalizedPathway === 'family';
  const showVulnerabilityCategory = !isAsylumApplicant && normalizedPathway === 'vulnerability';

  return (
    <DocumentsTabSource
      docs={docs}
      onDocsChange={setDocs}
      highlightDocId={highlightDocId}
      onClearHighlight={onClearHighlight}
      showAsylumCategory={showAsylumCategory}
      showEmploymentCategory={showEmploymentCategory}
      showFamilyCategory={showFamilyCategory}
      showVulnerabilityCategory={showVulnerabilityCategory}
      asylumScenario=""
      subRoute={String(subRoute || '')}
      prorrogaType=""
      arrivalDate=""
      criminalCertificates={criminalCertificates}
      onCriminalCertificatesChange={setCriminalCertificates}
      countriesOfResidence={countriesOfResidence}
      onCountriesChange={setCountriesOfResidence}
      passportIdFields={passportIdFields}
      onPassportIdChange={(key, value) =>
        setPassportIdFields((prev) => ({ ...prev, [key]: value }))
      }
      employmentFields={employmentFields}
      onEmploymentFieldsChange={(patch) =>
        setEmploymentFields((prev) => ({ ...prev, ...patch }))
      }
      oarCaseNumber=""
      onOarCaseNumberChange={() => { }}
      categoryReview={categoryReview}
      onCategoryReviewChange={(category, status) => {
        setCategoryReview((prev) => ({ ...prev, [category]: status }));
        setDocs((prev) =>
          (prev || []).map((doc) =>
            doc.category === category && doc.uploaded
              ? { ...doc, reviewStatus: status, rejectionReason: status === 'rejected' ? doc.rejectionReason : undefined }
              : doc,
          ),
        );
        if (category === 'criminal') {
          setCriminalCertificates((prev) =>
            (prev || []).map((cert) => ({
              ...cert,
              reviewStatus: status,
              rejectionReason: status === 'rejected' ? cert.rejectionReason : undefined,
            })),
          );
        }
      }
      }
      userId={userId}
      sessionId={sessionId}
      documents={documents}
      archivedDocuments={archivedDocuments}
      onRefresh={onRefresh}
    />
  );
}
